import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { MODULES, MODULE_KEYS } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const navItem = (isActive) =>
  `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
    isActive ? "bg-[#002FA7] text-white font-medium" : "text-slate-300 hover:bg-slate-700/60 hover:text-white"
  }`;

const GROUPS = [
  { key: "samples", label: "Samples & Traceability", icon: "TestTubes",
    modules: ["samples", "traceability", "tat", "critical_alerts", "rejections", "repeats", "archives"] },
  { key: "quality", label: "Quality & Equipment", icon: "ShieldCheck",
    modules: ["qc", "equipment", "proficiency", "sops"] },
  { key: "store", label: "Sub-store", icon: "Boxes", modules: [] },
  { key: "people", label: "Personnel & Training", icon: "Users", modules: [] },
  { key: "control", label: "Control Panel", icon: "SlidersHorizontal", modules: [] },
];

const NavGroup = ({ label, icon, groupKey, activeGroup, children, suffix }) => {
  const Icon = Icons[icon] || Icons.Circle;
  const stored = () => {
    try { return localStorage.getItem(`labtrack.nav.${groupKey}`) === "1"; } catch { return false; }
  };
  const [open, setOpen] = useState(() => stored() || activeGroup === groupKey);

  useEffect(() => { if (activeGroup === groupKey) setOpen(true); }, [activeGroup, groupKey]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(`labtrack.nav.${groupKey}`, next ? "1" : "0"); } catch { /* private mode */ }
  };

  return (
    <div className="pt-1">
      <button onClick={toggle} data-testid={`nav-group-${groupKey}${suffix}`} type="button"
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
          activeGroup === groupKey ? "text-white bg-slate-700/40" : "text-slate-300 hover:bg-slate-700/60 hover:text-white"}`}>
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{label}</span>
        {open ? <Icons.ChevronDown className="h-3.5 w-3.5" /> : <Icons.ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-2 border-l border-slate-700 space-y-1"
          data-testid={`nav-group-items-${groupKey}${suffix}`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout, allowedModules, features } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [labName, setLabName] = useState("");

  const activeGroup = (() => {
    const path = location.pathname;
    if (path.startsWith("/store")) return "store";
    if (path.startsWith("/personnel") || path.startsWith("/trends")) return "people";
    if (["/settings", "/templates", "/automation", "/labs", "/admin", "/manual"].some((p) => path.startsWith(p)))
      return "control";
    if (path.startsWith("/abdm")) return null;
    if (path.startsWith("/patients")) return "samples";
    const m = path.startsWith("/m/") ? path.split("/")[2] : null;
    return GROUPS.find((g) => g.modules.includes(m))?.key || null;
  })();

  useEffect(() => {
    api.get("/labs/mine").then(({ data }) => setLabName(data?.name || ""))
      .catch((e) => console.warn("lab lookup skipped:", e));
  }, [user?.user_id]);
  const canSee = (m) => user?.role === "admin" || allowedModules.includes(m);
  const hasFeature = (f) => user?.role === "admin" || features.includes(f);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const buildSidebar = (suffix) => (
    <aside className="w-64 shrink-0 bg-[#1E293B] border-r border-slate-700 flex flex-col h-full">
      <div className="p-4 border-b border-slate-700">
        <Logo dark />
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid={`sidebar-nav${suffix}`}>
        <NavLink to="/dashboard" className={({ isActive }) => navItem(isActive)} data-testid={`nav-dashboard${suffix}`} onClick={() => setOpen(false)}>
          <Icons.LayoutDashboard className="h-4 w-4" /> Dashboard
        </NavLink>

        <NavLink to="/abdm" className={({ isActive }) => navItem(isActive)} data-testid={`nav-abdm${suffix}`} onClick={() => setOpen(false)}>
          <Icons.ShieldCheck className="h-4 w-4" /> ABDM Integration
        </NavLink>

        {GROUPS.filter((g) => g.key === "samples" || g.key === "quality").map((g) => {
          const keys = g.modules.filter((k) => MODULE_KEYS.includes(k) && canSee(k));
          if (!keys.length) return null;
          return (
            <NavGroup key={g.key} groupKey={g.key} label={g.label} icon={g.icon}
              activeGroup={activeGroup} suffix={suffix}>
              {keys.map((key) => {
                const Icon = Icons[MODULES[key].icon] || Icons.Circle;
                return (
                  <NavLink key={key} to={`/m/${key}`} className={({ isActive }) => navItem(isActive)}
                    data-testid={`nav-${key}${suffix}`} onClick={() => setOpen(false)}>
                    <Icon className="h-4 w-4" /> {MODULES[key].label}
                  </NavLink>
                );
              })}
              {g.key === "samples" && canSee("samples") && (
                <NavLink to="/patients" className={({ isActive }) => navItem(isActive)}
                  data-testid={`nav-patients${suffix}`} onClick={() => setOpen(false)}>
                  <Icons.Users className="h-4 w-4" /> Patient Directory
                </NavLink>
              )}
            </NavGroup>
          );
        })}

        {(canSee("inventory") || canSee("indents")) && (
          <NavGroup groupKey="store" label="Sub-store" icon="Boxes" activeGroup={activeGroup} suffix={suffix}>
            {canSee("inventory") && <>
            <NavLink to="/store" end className={({ isActive }) => navItem(isActive)} data-testid={`nav-store${suffix}`} onClick={() => setOpen(false)}>
              <Icons.Boxes className="h-4 w-4" /> Inventory Overview
            </NavLink>
            <NavLink to="/store/items" className={({ isActive }) => navItem(isActive)} data-testid={`nav-store-items${suffix}`} onClick={() => setOpen(false)}>
              <Icons.Library className="h-4 w-4" /> Item Master
            </NavLink>
            <NavLink to="/store/transactions" className={({ isActive }) => navItem(isActive)} data-testid={`nav-store-txns${suffix}`} onClick={() => setOpen(false)}>
              <Icons.ArrowLeftRight className="h-4 w-4" /> Stock Transactions
            </NavLink>
            </>}
            {canSee("indents") && (
              <NavLink to="/store/indents" className={({ isActive }) => navItem(isActive)} data-testid={`nav-store-indents${suffix}`} onClick={() => setOpen(false)}>
                <Icons.ShoppingCart className="h-4 w-4" /> Indent Planner
              </NavLink>
            )}
          </NavGroup>
        )}

        <NavGroup groupKey="people" label="Personnel & Training" icon="Users"
          activeGroup={activeGroup} suffix={suffix}>
          <NavLink to="/personnel" className={({ isActive }) => navItem(isActive)} data-testid={`nav-personnel${suffix}`} onClick={() => setOpen(false)}>
            <Icons.Users className="h-4 w-4" /> Personnel
          </NavLink>
          <NavLink to="/trends" className={({ isActive }) => navItem(isActive)} data-testid={`nav-trends${suffix}`} onClick={() => setOpen(false)}
            style={hasFeature("trends") ? undefined : { display: "none" }}>
            <Icons.TrendingUp className="h-4 w-4" /> Trend Analysis
          </NavLink>
        </NavGroup>

        <NavGroup groupKey="control" label="Control Panel" icon="SlidersHorizontal"
          activeGroup={activeGroup} suffix={suffix}>
          <NavLink to="/manual" className={({ isActive }) => navItem(isActive)}
            data-testid={`nav-manual${suffix}`} onClick={() => setOpen(false)}>
            <Icons.BookOpen className="h-4 w-4" /> User Manual
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => navItem(isActive)} data-testid={`nav-settings${suffix}`} onClick={() => setOpen(false)}>
            <Icons.SlidersHorizontal className="h-4 w-4" /> Dropdown Settings
          </NavLink>
          <NavLink to="/templates" className={({ isActive }) => navItem(isActive)} data-testid={`nav-templates${suffix}`} onClick={() => setOpen(false)}>
            <Icons.ClipboardList className="h-4 w-4" /> Test Templates
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/automation" className={({ isActive }) => navItem(isActive)} data-testid={`nav-automation${suffix}`} onClick={() => setOpen(false)}>
              <Icons.Mail className="h-4 w-4" /> Automation & Reports
            </NavLink>
          )}
          {user?.is_primary_admin && (
            <NavLink to="/labs" className={({ isActive }) => navItem(isActive)} data-testid={`nav-labs${suffix}`} onClick={() => setOpen(false)}>
              <Icons.Building2 className="h-4 w-4" /> Cross-lab Overview
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({ isActive }) => navItem(isActive)} data-testid={`nav-admin${suffix}`} onClick={() => setOpen(false)}>
              <Icons.ShieldCheck className="h-4 w-4" /> Admin Panel
            </NavLink>
          )}
        </NavGroup>
      </nav>
      <div className="p-3 border-t border-slate-700">
        <div className="mb-3"><ThemeToggle label testId={`theme-toggle${suffix}`} className="w-full justify-center" /></div>
        <p className="text-xs text-slate-300 truncate" data-testid={`sidebar-user-email${suffix}`}>{user?.email}</p>
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{user?.role}</p>
        {labName && (
          <p className="text-[11px] text-slate-400 truncate mb-2" data-testid={`sidebar-lab${suffix}`}>{labName}</p>
        )}
        <Button variant="outline" size="sm" onClick={onLogout} data-testid={`logout-btn${suffix}`}
          className="w-full bg-transparent border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white">
          <Icons.LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[#FDFDFD]">
      <div className="hidden lg:flex h-screen sticky top-0">{buildSidebar("")}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="h-full">{buildSidebar("-mobile")}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E5E7EB] px-4 py-3 flex items-center justify-between">
          <Logo />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-2 rounded-md border border-[#E5E7EB]">
            <Icons.Menu className="h-5 w-5" />
          </button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]">
          <Outlet />
          <Footer />
        </main>
      </div>
    </div>
  );
}
